import { Field } from 'formik';
import { useContext } from 'react';
import { PageContext, useSanitizedTranslation } from '..';
import { InputType } from '../types';
import { isEnterpriseOrShopper } from '../utils';
import { StringSchema } from 'yup';
export interface IFormField {
  type: InputType;
  name: string;
  placeholder: string;
  width?: 'full' | 'half' | 'third';
  customValidation?: StringSchema;
}

export type FormFieldProps = IFormField & {
  handleChange: (e: any) => void;
  value: string | string[];
  focusedElementId: string | undefined;
  styleOverride?: string;
  errorId?: string;
  errorText?: string;
  hideLabel?: boolean;
  accentColor?: string;
  hoverColor?: string;
};

export const FormField = ({
  name,
  placeholder,
  type,
  handleChange,
  value,
  errorId,
  errorText,
  width = 'half',
  styleOverride = '',
  hideLabel = false,
  accentColor,
  hoverColor,
}: FormFieldProps) => {
  const { t } = useSanitizedTranslation();
  const { pageCtx } = useContext(PageContext);
  const { host } = pageCtx;

  const focusBorder = isEnterpriseOrShopper(host) ? 'focus:border-xumoAegean' : 'focus:border-xumoBerry';

  const wrapperClasses = `flex flex-col ${width === 'full' ? 'w-full' : width === 'third' ? 'w-full md:w-1/3' : 'w-full md:w-1/2'}`;

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (type) {
    case InputType.Textarea:
      return (
        <div className={wrapperClasses}>
          {!hideLabel && (
            <label data-i18n htmlFor={name}>
              {t(placeholder)}
            </label>
          )}
          <textarea
            className={`
              mt-2 w-full resize-none rounded-md p-2 outline outline-2 outline-[#ccc] placeholder:text-sm
              ${hoverColor ? `hover:outline-${hoverColor}` : 'hover:outline-xumoTeal'}
              ${accentColor ? `focus:outline-${accentColor}` : 'focus:outline-xumoBerry'}
              ${styleOverride}`}
            name={name}
            value={value}
            onChange={handleChange}
            aria-errormessage={errorId}
            placeholder={t(placeholder)}
            data-i18n={placeholder}
          />
          {errorText && (
            <p id={errorId} className="mt-2 text-sm italic text-xumoBerry" data-i18n={errorText}>
              {t(errorText)}
            </p>
          )}
        </div>
      );
    case InputType.Checkbox: {
      const renderedHtml = t(placeholder);

      return (
        <div className="relative w-full">
          <div className="mt-5 inline-flex items-start">
            <label className="relative flex cursor-pointer items-start" htmlFor={name}>
              <Field
                className={`
              border-[#ccc]shadow peer h-5 w-5 cursor-pointer appearance-none rounded border-2 transition-all placeholder:text-sm checked:border-slate-800 checked:bg-slate-800 hover:shadow-md hover:outline-xumoTeal focus:outline-xumoBerry focus:placeholder:visible`}
                name={name}
                id={name}
                type={type}
                onChange={handleChange}
                aria-errormessage={errorId}
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-white opacity-0 peer-checked:opacity-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" stroke-width="1">
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </span>
            </label>
            <label className="ml-2 cursor-pointer text-sm" htmlFor={name}>
              <div>
                <p dangerouslySetInnerHTML={{ __html: placeholder }}></p>
              </div>
            </label>
          </div>
        </div>
      );
    }

    default:
      return (
        <div className={wrapperClasses}>
          {!hideLabel && (
            <label data-i18n htmlFor={name} className="mb-2">
              {t(placeholder)}
            </label>
          )}
          <Field
            className={`w-full rounded-md p-2 outline outline-2 outline-[#ccc] placeholder:text-sm
              ${hoverColor ? `hover:outline-${hoverColor}` : 'hover:outline-xumoTeal'}
              ${accentColor ? `focus:outline-${accentColor}` : 'focus:outline-xumoBerry'}
              ${styleOverride}`}
            name={name}
            placeholder={t(placeholder)}
            value={value}
            type={type}
            onChange={handleChange}
            aria-errormessage={errorId}
            data-i18n={placeholder}
          />
          {errorText && (
            <p id={errorId} className="mt-2 text-sm italic text-xumoBerry" data-i18n>
              {t(errorText)}
            </p>
          )}
        </div>
      );
  }
};
